import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, adminCompanyPath } from '../../lib/api-client';
import { useCompany } from '../../contexts/CompanyContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

interface SellerItem {
  id: string;
  code: string;
  name: string;
  bankCode: string;
  accountNo: string;
  status: string;
  payinFeePercent: number;
  payoutFeePercent: number;
  syncedAt: string | null;
  hasAccount: boolean;
  username: string | null;
}

interface CredentialInfo {
  username: string;
  tempPassword: string;
  sellerName: string;
}

function CredentialDialog({
  credential,
  onClose,
}: {
  credential: CredentialInfo;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    const text = `${t('sellers.credentialCopyFormat', {
      name: credential.sellerName,
      username: credential.username,
      password: credential.tempPassword,
    })}`;
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
          {t('sellers.credentialTitle')}
        </h3>

        <div className="mb-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          {t('sellers.credentialWarning')}
        </div>

        <div className="mb-4 space-y-3">
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('sellers.name')}
            </label>
            <div className="font-medium text-gray-900 dark:text-gray-100">
              {credential.sellerName}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('auth.username')}
            </label>
            <div className="rounded bg-gray-100 px-3 py-2 font-mono text-sm dark:bg-gray-700 dark:text-gray-100">
              {credential.username}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">
              {t('sellers.tempPassword')}
            </label>
            <div className="rounded bg-gray-100 px-3 py-2 font-mono text-sm font-bold dark:bg-gray-700 dark:text-gray-100">
              {credential.tempPassword}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Button onClick={copyToClipboard} className="flex-1">
            {copied ? t('sellers.copied') : t('sellers.copyCredentials')}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FeeDisplay({ seller }: { seller: SellerItem }) {
  const { t } = useTranslation();
  const noFees = seller.payinFeePercent === 0 && seller.payoutFeePercent === 0;
  if (noFees) {
    return <span className="text-amber-600 dark:text-amber-400 text-xs">{t('sellers.notSet')}</span>;
  }
  return (
    <div className="text-xs space-y-0.5">
      <div>{t('sellers.payinFee')}: {seller.payinFeePercent}%</div>
      <div>{t('sellers.payoutFee')}: {seller.payoutFeePercent}%</div>
    </div>
  );
}

export function SellerList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeCompanyId } = useCompany();
  const [credential, setCredential] = useState<CredentialInfo | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'sellers', activeCompanyId],
    queryFn: () => apiGet<{ items: SellerItem[]; total: number }>(adminCompanyPath('/sellers', activeCompanyId!)),
    enabled: !!activeCompanyId,
  });

  const resetMutation = useMutation({
    mutationFn: (sellerId: string) =>
      apiPost<{ tempPassword: string; username: string; message: string }>(
        adminCompanyPath(`/sellers/${sellerId}/reset-password`, activeCompanyId!),
      ),
    onSuccess: (res, sellerId) => {
      if (res.ok) {
        const seller = sellers.find((s) => s.id === sellerId);
        setCredential({
          username: res.data.username,
          tempPassword: res.data.tempPassword,
          sellerName: seller?.name ?? '',
        });
        setResetError(null);
        queryClient.invalidateQueries({ queryKey: ['admin', 'sellers'] });
      } else {
        setResetError(res.error.message);
      }
    },
  });

  const createAccountMutation = useMutation({
    mutationFn: (sellerId: string) =>
      apiPost<{ username: string; tempPassword: string; sellerName: string; message: string }>(
        adminCompanyPath(`/sellers/${sellerId}/create-account`, activeCompanyId!),
      ),
    onSuccess: (res) => {
      if (res.ok) {
        setCredential({
          username: res.data.username,
          tempPassword: res.data.tempPassword,
          sellerName: res.data.sellerName,
        });
        setResetError(null);
        queryClient.invalidateQueries({ queryKey: ['admin', 'sellers'] });
      } else {
        setResetError(res.error.message);
      }
    },
  });

  const sellers = data?.ok ? data.data.items : [];

  const handleResetPassword = (seller: SellerItem) => {
    if (window.confirm(t('sellers.confirmReset', { name: seller.name }))) {
      resetMutation.mutate(seller.id);
    }
  };

  const handleCreateAccount = (seller: SellerItem) => {
    if (window.confirm(t('sellers.confirmCreateAccount', { name: seller.name }))) {
      createAccountMutation.mutate(seller.id);
    }
  };



  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        {t('sellers.title')}
      </h1>

      {!activeCompanyId && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200">
          {t('company.noCompanySelected')}
        </div>
      )}

      {resetError && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">
          {resetError}
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle>{t('sellers.title')}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && (
            <div className="p-6 text-gray-400">{t('common.loading')}</div>
          )}
          {error && (
            <div className="p-6 text-red-600">{t('common.error')}</div>
          )}
          {!isLoading && sellers.length === 0 && (
            <div className="p-6 text-gray-400">{t('common.noData')}</div>
          )}
          {sellers.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('sellers.code')}</TableHead>
                  <TableHead>{t('sellers.name')}</TableHead>
                  <TableHead>{t('sellers.status')}</TableHead>
                  <TableHead>{t('sellers.payinFee')} / {t('sellers.payoutFee')}</TableHead>
                  <TableHead>{t('admin.syncStatus')}</TableHead>
                  <TableHead>{t('sellers.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sellers.map((seller) => (
                  <TableRow key={seller.id}>
                    <TableCell className="font-mono">{seller.code}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => navigate(`/admin/sellers/${seller.id}`)}
                        className="text-left font-medium text-blue-600 hover:underline dark:text-blue-400"
                      >
                        {seller.name}
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={seller.status === 'active' ? 'success' : 'danger'}
                      >
                        {t(`common.${seller.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <FeeDisplay seller={seller} />
                    </TableCell>
                    <TableCell>
                      {seller.syncedAt ? (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          {new Date(seller.syncedAt).toLocaleString()}
                        </span>
                      ) : (
                        <Badge variant="warning">{t('admin.notSynced')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {seller.hasAccount ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetPassword(seller)}
                          disabled={resetMutation.isPending}
                        >
                          {t('sellers.resetPassword')}
                        </Button>
                      ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleCreateAccount(seller)}
                          disabled={createAccountMutation.isPending}
                          className="text-blue-600 dark:text-blue-400"
                        >
                          {t('sellers.createAccount')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {credential && (
        <CredentialDialog
          credential={credential}
          onClose={() => setCredential(null)}
        />
      )}
    </div>
  );
}
